// YERİ: src/app/domains/site/sections/section.model.ts
// Section bileşenleri için ortak model + config yardımcıları + tema.
// Her section standalone bir bileşendir, parse edilmiş config'i input olarak alır.
// "Lawyer görünümü" = section seçimi + tema (CSS vars) + preset default içerik.

export type SiteConfig = Record<string, any>;

/** JSON config string → obje (güvenli). */
export function parseConfig(json: string | null | undefined): SiteConfig {
  if (!json) return {};
  try { const v = JSON.parse(json); return (v && typeof v === 'object') ? v : {}; }
  catch { return {}; }
}

/** config[key] ?? fallback (boş string de fallback'e düşer). */
export function cfg<T>(config: SiteConfig, key: string, fallback: T): T {
  const v = config?.[key];
  if (v === undefined || v === null || v === '') return fallback;
  return v as T;
}

/** Section bileşenlerinin ortak arayüzü — host parse edip [config] geçer. */
export interface SiteSectionComponent {
  config: SiteConfig;
}

/** Site teması — renderer kök elemanına CSS custom property olarak basılır.
 *  Section'lar Tailwind arbitrary value ile tüketir: text-[color:var(--site-primary)] vb.
 *  İleride ClientTheme'den doldurulur; şimdilik sektör preset'i verir. */
export interface SiteTheme {
  primary: string;       // ana renk
  primaryDark: string;
  accent: string;        // vurgu (ör. avukat altın)
  headingFont: string;   // başlık font-family
  bodyFont: string;
  // ── İnce ayarlar / erişilebilirlik (opsiyonel; özel tema) ──
  fontScale?: number;        // genel yazı ölçeği (1 = normal, 1.1, 1.25…) — renderer 'zoom' ile uygular
  italicHeadings?: boolean;  // başlıklar italik
  underlineLinks?: boolean;  // tüm bağlantıların altı çizili (erişilebilirlik)
  highContrast?: boolean;    // yüksek kontrast (koyu metin + güçlü kenarlık)
  /**
   * Site genelinde gövde metni ve başlık rengi. Boşsa her bölüm kendi
   * rengini kullanır (bugünkü davranış).
   *
   * DİKKAT: bu değer !important ile uygulanır — bu derlemede Tailwind
   * yardımcı sınıfları da !important taşıyor, miras almak yetmiyor. Dolayısıyla
   * koyu zeminli bölümlerde (hero gibi) beyaz metin de bu renge döner;
   * o bölüme BÖLÜM AYARLARINDAN kendi rengini vermek gerekir (bölüm
   * değeri tema değerini ezer).
   */
  textColor?: string;

  // ── Görsel kimlik (şablon varyantlarını ayıran şeyler) ──
  /**
   * Arka plan deseni. Aynı bölüm dizilimi bile farklı desenle başka bir
   * tasarım gibi okunuyor; şablonları renkten fazlasıyla ayıran ilk kaldıraç.
   */
  pattern?: PatternKey;
  /** Köşe yuvarlaklığı — keskin kurumsal ile yumuşak modern arasındaki fark. */
  radius?: 'sharp' | 'soft' | 'round';
  /** Buton biçimi. */
  buttonStyle?: 'solid' | 'pill' | 'outline';
}

/** Desen anahtarları — SVG geometrisi kodda üretilir, stok görsel YOK. */
export type PatternKey = 'none' | 'dots' | 'grid' | 'diagonal' | 'rings' | 'waves' | 'plus'
  | 'hex' | 'zigzag' | 'confetti' | 'arc';

const RADIUS_MAP: Record<string, [string, string]> = {
  //            genel kart      buton
  sharp: ['0.125rem', '0.125rem'],
  soft:  ['1rem',     '0.5rem'],
  round: ['1.75rem',  '9999px'],
};

/**
 * Desenin SVG'sini `background-image` değerine çevirir.
 *
 * Renk tema renginden gelir ve düşük opaklıkla basılır: desen metnin arkasında
 * kalmalı, onunla yarışmamalı. `#` karakteri data URI'de kaçırılmak zorunda —
 * kaçırılmazsa Chrome deseni sessizce çizmez.
 */
export function patternCss(key: PatternKey | undefined, color: string, opacity = 0.09): string {
  if (!key || key === 'none') return 'none';
  const c = encodeURIComponent(color);
  const o = opacity;
  const svg = (body: string, size = 24) =>
    `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}' viewBox='0 0 ${size} ${size}'%3E${body}%3C/svg%3E")`;

  switch (key) {
    case 'dots':
      return svg(`%3Ccircle cx='3' cy='3' r='1.6' fill='${c}' fill-opacity='${o}'/%3E`, 18);
    case 'grid':
      return svg(`%3Cpath d='M0 0H32M0 0V32' stroke='${c}' stroke-opacity='${o}' stroke-width='1'/%3E`, 32);
    case 'diagonal':
      return svg(`%3Cpath d='M-4 12L12 -4M0 24L24 0M12 28L28 12' stroke='${c}' stroke-opacity='${o}' stroke-width='2'/%3E`, 24);
    case 'rings':
      return svg(`%3Ccircle cx='20' cy='20' r='14' fill='none' stroke='${c}' stroke-opacity='${o}' stroke-width='1.5'/%3E`, 40);
    case 'waves':
      return svg(`%3Cpath d='M0 12c6 0 6-8 12-8s6 8 12 8 6-8 12-8' fill='none' stroke='${c}' stroke-opacity='${o}' stroke-width='1.5'/%3E`, 36);
    case 'plus':
      return svg(`%3Cpath d='M12 8v8M8 12h8' stroke='${c}' stroke-opacity='${o}' stroke-width='1.5' stroke-linecap='round'/%3E`, 24);
    case 'hex':
      // Altıgen petek — teknik/modern dil.
      return svg(`%3Cpath d='M14 2l12 7v14l-12 7-12-7V9z' fill='none' stroke='${c}' stroke-opacity='${o}' stroke-width='1.5'/%3E`, 28);
    case 'zigzag':
      return svg(`%3Cpath d='M0 16l8-8 8 8 8-8' fill='none' stroke='${c}' stroke-opacity='${o}' stroke-width='2' stroke-linecap='round'/%3E`, 32);
    case 'confetti':
      // Serbest yerleşimli kısa çizgiler — oyuncu/yaratıcı dil.
      return svg(
        `%3Cpath d='M6 8l3-4M22 10l2-5M14 26l4-3M30 22l3 4M9 30l-3 4' stroke='${c}' stroke-opacity='${o}' stroke-width='2' stroke-linecap='round'/%3E`
        + `%3Ccircle cx='28' cy='7' r='2' fill='${c}' fill-opacity='${o}'/%3E%3Ccircle cx='7' cy='20' r='1.6' fill='${c}' fill-opacity='${o}'/%3E`, 40);
    case 'arc':
      return svg(`%3Cpath d='M0 32a32 32 0 0 1 32-32' fill='none' stroke='${c}' stroke-opacity='${o}' stroke-width='1.5'/%3E`, 32);
    default:
      return 'none';
  }
}

/** Hex rengi belirtilen oranda koyulaştır (primaryDark türetmek için). */
export function darken(hex: string, amount = 0.22): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const r = Math.max(0, Math.round(((n >> 16) & 255) * (1 - amount)));
  const g = Math.max(0, Math.round(((n >> 8) & 255) * (1 - amount)));
  const b = Math.max(0, Math.round((n & 255) * (1 - amount)));
  return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
}

export const DEFAULT_THEME: SiteTheme = {
  primary: '#1e3a5f', primaryDark: '#15293f', accent: '#b08d57',
  headingFont: `'Playfair Display', Georgia, serif`,
  bodyFont: `'Inter', system-ui, sans-serif`,
};

/** Tema → CSS custom property nesnesi (renderer [style] ile basar). */
export function themeVars(t: SiteTheme): Record<string, string> {
  const [radius, btnRadius] = RADIUS_MAP[t.radius ?? 'soft'] ?? RADIUS_MAP['soft'];
  return {
    '--site-primary': t.primary,
    '--site-primary-dark': t.primaryDark,
    '--site-accent': t.accent,
    '--site-head-font': t.headingFont,
    '--site-body-font': t.bodyFont,
    '--site-radius': radius,
    '--site-btn-radius': t.buttonStyle === 'pill' ? '9999px' : btnRadius,
    // İKİ desen değişkeni: zemine göre seçilir.
    //   --site-pattern       → AÇIK zeminler; marka renginde çizilir
    //   --site-pattern-light → KOYU zeminler (hero, slider); beyaz çizilir
    // Tek değişken olsaydı hero'da mor zemine mor desen düşer ve desen hiç
    // görünmezdi (ilk denemede tam bu oldu).
    '--site-pattern': patternCss(t.pattern, t.primary, 0.1),
    '--site-pattern-light': patternCss(t.pattern, '#ffffff', 0.16),
  };
}
